import type { FC } from "react";

import { useTranslation } from "react-i18next";

import { Text } from "shared/uikit";
import { TextStylesEnum, TextVariantsEnum } from "shared/uikit/types";
import { formatMoney } from "utils/formatMoney";

import CollectedAmountCell from "./CollectedAmountCell";
import PaymentRequestProgressCell from "./PaymentRequestProgressCell";
import type { PaymentRequestAmountSummary } from "./amountSummary";

interface AmountStatsProps {
	summaries: PaymentRequestAmountSummary[];
}

const AmountStats: FC<AmountStatsProps> = ({ summaries }) => {
	const { t } = useTranslation();

	return (
		<div className="w-full p-4 flex flex-col gap-4 bg-gray-50">
			{summaries.map(summary => (
				<div key={summary.currency} className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
					<div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
						<div>
							<Text variants={TextVariantsEnum.Caption3} style={TextStylesEnum.Hint}>
								{t("currency")}
							</Text>
							<Text variants={TextVariantsEnum.H7} className="mt-1 uppercase">
								{summary.currency}
							</Text>
						</div>
						<div className="rounded-full bg-gray-100 px-3 py-1">
							<Text variants={TextVariantsEnum.Caption3} className="uppercase">
								{summary.currency}
							</Text>
						</div>
					</div>
					<div className="grid grid-cols-2 gap-3 p-4">
						<div className="rounded-md border border-gray-100 bg-gray-50 p-3">
							<Text variants={TextVariantsEnum.Caption3} style={TextStylesEnum.Hint}>
								{t("payment:total_amount")}
							</Text>
							<Text variants={TextVariantsEnum.Number} className="mt-2">
								{formatMoney(summary.totalAmount, { currency: summary.currency })}
							</Text>
						</div>
						<div className="rounded-md border border-gray-100 bg-gray-50 p-3">
							<Text variants={TextVariantsEnum.Caption3} style={TextStylesEnum.Hint}>
								{t("payment:remaining_amount")}
							</Text>
							<Text variants={TextVariantsEnum.Number} className="mt-2">
								{formatMoney(summary.remainingAmount, { currency: summary.currency })}
							</Text>
						</div>
						<div className="rounded-md border border-gray-100 bg-gray-50 p-3">
							<Text variants={TextVariantsEnum.Caption3} style={TextStylesEnum.Hint} className="mb-2">
								{t("collected")}
							</Text>
							<CollectedAmountCell summaries={[summary]} />
						</div>
						<div className="rounded-md border border-gray-100 bg-gray-50 p-3">
							<Text variants={TextVariantsEnum.Caption3} style={TextStylesEnum.Hint} className="mb-2">
								{t("progress")}
							</Text>
							<PaymentRequestProgressCell summaries={[summary]} />
						</div>
					</div>
				</div>
			))}
		</div>
	);
};

export default AmountStats;
